import dynamic from 'next/dynamic';
import React, { useContext, useState } from 'react';

import { Flex, rem, useBreakpointValue } from '@vimeo/bokeh';
import { Navigation } from '@vimeo/navigation';
import { AccountMenu } from '@vimeo/navigation/components/AccountMenu';
import { ActivityDropdown } from '@vimeo/navigation/components/ActivityDropdown';
import { HelpCTA } from '@vimeo/navigation/components/HelpCTA';
import { Login, Join, LoginJoinModalContext } from '@vimeo/navigation/components/LoginJoin';
import { NewContentMenu } from '@vimeo/navigation/components/NewContentMenu';
import { PricingCTA } from '@vimeo/navigation/components/PricingCTA';
import { Search } from '@vimeo/navigation/components/Search';
import { SideNavToggle } from '@vimeo/navigation/components/SideNavToggle';
import { StockCart } from '@vimeo/navigation/components/StockCart';
import { useNewCtaAssignment } from '@vimeo/navigation/hooks/use-new-cta-assignment';
import { ViewerContext } from '@vimeo/viewer';

const LoginJoinModal = dynamic(
  async () => (await import('@vimeo/navigation/components/LoginJoin/Modal')).LoginJoinModal,
);

export const DefaultNavigation = ({
  fixed = true,
  hasSearch = true,
  hasThemeSupport = true,
  isSideNavActive,
  setIsSideNavActive,
  hasUploader = false,
  alwaysMinimiseSearch = false,
}: {
  fixed?: boolean;
  hasSearch?: boolean;
  hasThemeSupport?: boolean;
  isSideNavActive?: boolean;
  setIsSideNavActive?: VoidFunction;
  hasUploader?: boolean;
  alwaysMinimiseSearch?: boolean;
}) => {
  const viewer = useContext(ViewerContext);
  const user = viewer?.user;

  const [isSearchOpen, setIsSearchOpen] = useState(false);

  let searchOnLeft = useBreakpointValue({ base: false, lg: true });
  const condense = useBreakpointValue({ base: true, md: false });
  const minify = useBreakpointValue({ base: true, sm: false });
  const searchFullWidth = isSearchOpen && condense;

  if (alwaysMinimiseSearch) {
    searchOnLeft = false;
  }
  const hideUpgrade = (!searchOnLeft && isSearchOpen) || minify;

  const [loginModalType, setLoginModalType] = useState<'login' | 'join' | undefined>();

  const isSimplifiedSite = viewer?.isSimplifiedSite;
  const isEnterpriseSite = viewer?.isEnterpriseSite;

  const content = [
    !hideUpgrade && <Navigation.Upgrade viewer={viewer} />,
    hasSearch && !searchOnLeft && (
      <Search withToggle updateGlobalNavSearchState={setIsSearchOpen} />
    ),
    !searchFullWidth && !isSimplifiedSite && (
      <>
        <HelpCTA />
        <ActivityDropdown />
        <StockCart itemsCount={viewer?.cart?.itemsCount} />
      </>
    ),
    viewer && !user && (
      <>
        {!hideUpgrade && !isEnterpriseSite && <PricingCTA />}
        {!(minify && isSearchOpen) && <Login />}
      </>
    ),
  ];
  const accountContent = [
    !searchFullWidth &&
      (user ? (
        <NewContentMenu hasUploader={hasUploader} />
      ) : (
        viewer && !isEnterpriseSite && <Join />
      )),
    !(minify && isSearchOpen) && <AccountMenu hasThemeSupport={hasThemeSupport} />,
    viewer && !user && loginModalType && <LoginJoinModal />,
  ];

  /**
   * Using shouldUseNewCta to gate the style used for the accountContent,
   * either to include it in the rightContent div or exclude
   */
  const shouldUseNewCta = useNewCtaAssignment();
  const rightContent = shouldUseNewCta ? [...content, ...accountContent] : content;

  return (
    <Navigation
      position={fixed ? 'sticky' : 'unset'}
      top="0"
      gap={{ base: 'sm', md: 'md', lg: '5' }}
      px={{ base: '3', md: 'lg' }}
      /** make the z-index one higher than the Chakra dropdown z-index of 1000 */
      zIndex="1001"
    >
      <Navigation.LeftContent
        width={searchOnLeft ? '100%' : 'unset'}
        gap={{ base: 'sm', md: 'md' }}
      >
        {setIsSideNavActive && (
          <SideNavToggle
            isSideNavActive={Boolean(isSideNavActive)}
            setIsSideNavActive={setIsSideNavActive}
          />
        )}
        <Navigation.VimeoLogo display={isSideNavActive ? 'none' : 'flex'} />
        {hasSearch && searchOnLeft && <Search />}
      </Navigation.LeftContent>
      <Navigation.RightContent
        justifyContent="end"
        width={!searchOnLeft ? '100%' : 'unset'}
        gap={{ base: 'sm', md: 'md' }}
      >
        <LoginJoinModalContext.Provider
          value={{ modal: loginModalType, setModal: setLoginModalType }}
        >
          <Flex
            alignItems="center"
            gap={{ base: 'xs', md: rem(12) }}
            flexGrow="1"
            justifyContent="end"
          >
            {rightContent}
          </Flex>
          {!shouldUseNewCta && accountContent}
        </LoginJoinModalContext.Provider>
      </Navigation.RightContent>
    </Navigation>
  );
};
